import React, { useState } from "react";
import { YouthTopProblemRating } from "../api/contracts";
import { formatPortalDate } from "../utils/dateTime";

export interface YouthTopProblemsTrendPoint {
  date: string;
  problem1Rating: number | null;
  problem2Rating: number | null;
  problem3Rating: number | null;
}

type YouthTopProblemsSeriesKey = Exclude<
  keyof YouthTopProblemsTrendPoint,
  "date"
>;

const youthTopProblemsSeries: ReadonlyArray<{
  key: YouthTopProblemsSeriesKey;
  label: string;
}> = [
  { key: "problem1Rating", label: "Client top problem 1" },
  { key: "problem2Rating", label: "Client top problem 2" },
  { key: "problem3Rating", label: "Client top problem 3" },
];

function validRating(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value >= 0 && value <= 10;
}

function average(values: readonly number[]): number | null {
  if (!values.length) return null;
  const result =
    values.reduce((total, value) => total + value, 0) / values.length;
  return Math.round(result * 10) / 10;
}

export function buildYouthTopProblemsTrendPoints(
  ratings: readonly YouthTopProblemRating[],
): YouthTopProblemsTrendPoint[] {
  const groups = new Map<
    string,
    Record<YouthTopProblemsSeriesKey, number[]>
  >();

  for (const rating of ratings) {
    const values = {
      problem1Rating: rating.problem1Rating,
      problem2Rating: rating.problem2Rating,
      problem3Rating: rating.problem3Rating,
    };
    if (!Object.values(values).some(validRating)) continue;

    const group = groups.get(rating.recordedOn) ?? {
      problem1Rating: [],
      problem2Rating: [],
      problem3Rating: [],
    };
    youthTopProblemsSeries.forEach(({ key }) => {
      const value = values[key];
      if (validRating(value)) group[key].push(value);
    });
    groups.set(rating.recordedOn, group);
  }

  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, group]) => ({
      date,
      problem1Rating: average(group.problem1Rating),
      problem2Rating: average(group.problem2Rating),
      problem3Rating: average(group.problem3Rating),
    }));
}

function formatValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function YouthTopProblemsTrendChart({
  points,
}: {
  points: readonly YouthTopProblemsTrendPoint[];
}) {
  const [activePointIndex, setActivePointIndex] = useState<number | null>(null);
  const [pinnedPointIndex, setPinnedPointIndex] = useState<number | null>(null);

  if (!points.length) {
    return (
      <div className="cp-chart-empty">
        <h3>No Youth Top Problems trend data yet</h3>
        <p>Client-reported Youth Top Problems ratings will appear here.</p>
      </div>
    );
  }

  const width = 760;
  const height = 340;
  const margin = { top: 24, right: 24, bottom: 62, left: 52 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const xAt = (index: number) =>
    margin.left +
    (points.length === 1
      ? plotWidth / 2
      : (index / (points.length - 1)) * plotWidth);
  const yAt = (value: number) => margin.top + ((10 - value) / 10) * plotHeight;
  const labelEvery = Math.max(1, Math.ceil(points.length / 8));
  const pathFor = (key: YouthTopProblemsSeriesKey) =>
    points
      .map((point, index) =>
        point[key] === null ? null : `${xAt(index)},${yAt(point[key])}`,
      )
      .filter((value): value is string => Boolean(value))
      .map((coordinate, index) => `${index === 0 ? "M" : "L"}${coordinate}`)
      .join(" ");
  const tooltipPointIndex = activePointIndex ?? pinnedPointIndex;
  const tooltipPoint =
    tooltipPointIndex === null ? null : points[tooltipPointIndex];
  const tooltipValues = tooltipPoint
    ? youthTopProblemsSeries.flatMap(({ key, label }) => {
        const value = tooltipPoint[key];
        return value === null ? [] : [{ key, label, value }];
      })
    : [];
  const tooltipWidth = 270;
  const tooltipHeight = 42 + tooltipValues.length * 22;
  const tooltipAnchorX =
    tooltipPointIndex === null ? margin.left : xAt(tooltipPointIndex);
  const tooltipAnchorY = tooltipPoint
    ? Math.min(...tooltipValues.map(({ value }) => yAt(value)))
    : margin.top;
  const tooltipX = Math.min(
    width - tooltipWidth - 8,
    Math.max(8, tooltipAnchorX + 14),
  );
  const tooltipY = Math.min(
    height - tooltipHeight - 8,
    Math.max(8, tooltipAnchorY - tooltipHeight - 12),
  );

  return (
    <figure className="cp-suds-chart cp-daily-mood-chart cp-youth-top-problems-chart">
      <div className="cp-suds-chart__plot">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Average client Youth Top Problems ratings by recorded date"
        >
          {[0, 2, 4, 6, 8, 10].map((value) => {
            const y = yAt(value);
            return (
              <g key={value}>
                <line
                  className="cp-suds-chart__grid"
                  x1={margin.left}
                  x2={width - margin.right}
                  y1={y}
                  y2={y}
                />
                <text
                  className="cp-suds-chart__axis-label"
                  x={margin.left - 12}
                  y={y + 4}
                  textAnchor="end"
                >
                  {value}
                </text>
              </g>
            );
          })}

          <line
            className="cp-suds-chart__axis"
            x1={margin.left}
            x2={margin.left}
            y1={margin.top}
            y2={height - margin.bottom}
          />
          <line
            className="cp-suds-chart__axis"
            x1={margin.left}
            x2={width - margin.right}
            y1={height - margin.bottom}
            y2={height - margin.bottom}
          />

          {points.map((point, index) =>
            index % labelEvery === 0 || index === points.length - 1 ? (
              <text
                className="cp-suds-chart__axis-label"
                key={point.date}
                x={xAt(index)}
                y={height - margin.bottom + 24}
                textAnchor="middle"
              >
                {formatPortalDate(point.date)}
              </text>
            ) : null,
          )}

          <text
            className="cp-suds-chart__axis-title"
            x={14}
            y={margin.top + plotHeight / 2}
            textAnchor="middle"
            transform={`rotate(-90 14 ${margin.top + plotHeight / 2})`}
          >
            Rating
          </text>
          <text
            className="cp-suds-chart__axis-title"
            x={margin.left + plotWidth / 2}
            y={height - 12}
            textAnchor="middle"
          >
            Recorded Date
          </text>

          {youthTopProblemsSeries.map(({ key }) => {
            const path = pathFor(key);
            return path ? (
              <path
                className="cp-suds-chart__line"
                data-series={key}
                d={path}
                key={key}
              />
            ) : null;
          })}

          {points.flatMap((point, index) =>
            youthTopProblemsSeries.flatMap(({ key, label }) => {
              const value = point[key];
              if (value === null) return [];
              return [
                <circle
                  className="cp-suds-chart__dot"
                  data-series={key}
                  key={`${point.date}-${key}`}
                  cx={xAt(index)}
                  cy={yAt(value)}
                  r={5}
                  tabIndex={0}
                  aria-label={`${label} average ${formatValue(value)} on ${formatPortalDate(point.date)}`}
                  onMouseEnter={() => setActivePointIndex(index)}
                  onMouseLeave={() => setActivePointIndex(null)}
                  onFocus={() => setActivePointIndex(index)}
                  onBlur={() => setActivePointIndex(null)}
                  onClick={() => {
                    setPinnedPointIndex((current) =>
                      current === index ? null : index,
                    );
                  }}
                />,
              ];
            }),
          )}

          {tooltipPoint ? (
            <g
              className="cp-daily-mood-chart__tooltip"
              role="status"
              aria-live="polite"
              aria-label={`Youth Top Problems averages on ${formatPortalDate(tooltipPoint.date)}`}
              transform={`translate(${tooltipX} ${tooltipY})`}
            >
              <rect width={tooltipWidth} height={tooltipHeight} rx={8} />
              <text className="cp-daily-mood-chart__tooltip-date" x={12} y={22}>
                {formatPortalDate(tooltipPoint.date)}
              </text>
              {tooltipValues.map(({ key, label, value }, index) => {
                const y = 44 + index * 22;
                return (
                  <g key={key}>
                    <circle
                      className="cp-daily-mood-chart__tooltip-swatch"
                      data-series={key}
                      cx={16}
                      cy={y - 4}
                      r={4}
                    />
                    <text
                      className="cp-daily-mood-chart__tooltip-label"
                      x={28}
                      y={y}
                    >
                      {label}
                    </text>
                    <text
                      className="cp-daily-mood-chart__tooltip-value"
                      x={tooltipWidth - 12}
                      y={y}
                      textAnchor="end"
                    >
                      {formatValue(value)}
                    </text>
                  </g>
                );
              })}
            </g>
          ) : null}
        </svg>
      </div>
      <figcaption className="cp-suds-chart__legend">
        {youthTopProblemsSeries.map(({ key, label }) => (
          <span data-series={key} key={key}>
            <i aria-hidden="true" /> {label}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}
